import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import { usersService } from '../services/users'
import { useAuth } from '../contexts/AuthContext'

const MfaSettingsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('common')
  const [step, setStep] = useState('loading') // loading, status, setup, verify, recovery-codes, disable, regenerate
  const [mfaStatus, setMfaStatus] = useState(null)
  const [setupData, setSetupData] = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [regenerateCode, setRegenerateCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  const { changeUserPassword } = useAuth()

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }
    return requirements
  }

  const isPasswordValid = (password) => {
    const reqs = validatePassword(password)
    return Object.values(reqs).every(Boolean)
  }

  const PasswordRequirements = ({ password }) => {
    const reqs = validatePassword(password)
    return (
      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
        <div style={{ color: reqs.length ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {reqs.length ? '✓' : '✗'} {t('security.password.requirements.length')}
        </div>
        <div style={{ color: reqs.uppercase ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {reqs.uppercase ? '✓' : '✗'} {t('security.password.requirements.uppercase')}
        </div>
        <div style={{ color: reqs.lowercase ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {reqs.lowercase ? '✓' : '✗'} {t('security.password.requirements.lowercase')}
        </div>
        <div style={{ color: reqs.digit ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {reqs.digit ? '✓' : '✗'} {t('security.password.requirements.digit')}
        </div>
        <div style={{ color: reqs.symbol ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {reqs.symbol ? '✓' : '✗'} {t('security.password.requirements.symbol')}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (isOpen) {
      fetchMfaStatus()
    }
  }, [isOpen])

  const fetchMfaStatus = async () => {
    setStep('loading')
    setError(null)
    try {
      const status = await usersService.getMfaStatus()
      setMfaStatus(status)
      setStep('status')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load MFA status')
      setStep('status')
    }
  }

  const handleStartSetup = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await usersService.setupMfa()
      setSetupData(data)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start MFA setup')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      setError(t('security.mfa.setup.enterCode'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await usersService.verifyMfa(verifyCode)
      setRecoveryCodes(result.recoveryCodes || [])
      setStep('recovery-codes')
      setVerifyCode('')
    } catch (err) {
      setError(err.response?.data?.error || t('security.mfa.setup.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const handleDisableMfa = async () => {
    if (!disableCode || disableCode.length !== 6) {
      setError(t('security.mfa.setup.enterCode'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      await usersService.disableMfa(disableCode)
      setDisableCode('')
      await fetchMfaStatus()
    } catch (err) {
      setError(err.response?.data?.error || t('security.mfa.setup.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerateCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      setError(t('security.mfa.setup.enterCode'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await usersService.regenerateRecoveryCodes(regenerateCode)
      setRecoveryCodes(result.recoveryCodes || [])
      setStep('recovery-codes')
      setRegenerateCode('')
    } catch (err) {
      setError(err.response?.data?.error || t('security.mfa.setup.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async () => {
    setPasswordError(null)
    setPasswordSuccess(false)

    if (!currentPassword) {
      setPasswordError(t('security.password.currentRequired'))
      return
    }

    if (!isPasswordValid(newPassword)) {
      setPasswordError(t('security.password.notMeetRequirements'))
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('security.password.noMatch'))
      return
    }

    if (currentPassword === newPassword) {
      setPasswordError(t('security.password.mustBeDifferent'))
      return
    }

    setChangingPassword(true)
    const result = await changeUserPassword(currentPassword, newPassword)
    setChangingPassword(false)

    if (result.success) {
      setPasswordSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => {
        setShowChangePassword(false)
        setPasswordSuccess(false)
      }, 2000)
    } else {
      setPasswordError(result.error)
    }
  }

  const handleClose = () => {
    setStep('loading')
    setMfaStatus(null)
    setSetupData(null)
    setVerifyCode('')
    setDisableCode('')
    setRegenerateCode('')
    setRecoveryCodes([])
    setError(null)
    setShowChangePassword(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError(null)
    setPasswordSuccess(false)
    onClose()
  }

  const copyRecoveryCodes = () => {
    const text = recoveryCodes.join('\n')
    navigator.clipboard.writeText(text)
  }

  const renderContent = () => {
    if (step === 'loading') {
      return (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          {t('security.loadingMfa')}
        </div>
      )
    }

    if (step === 'status') {
      return (
        <>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              marginBottom: '1.5rem',
              borderRadius: '4px',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              {error}
            </div>
          )}

          {mfaStatus?.tenantMfaMode === 'off' ? (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>{t('security.mfa.title')}</h3>
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--color-border)',
                borderRadius: '4px',
                color: 'var(--color-secondary)'
              }}>
                {t('security.mfa.notAvailable')}
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.5rem' }}>{t('security.mfa.title')}</h3>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  {t('security.mfa.description')}
                </p>

                <div style={{
                  padding: '1rem',
                  backgroundColor: mfaStatus?.mfaEnabled ? '#d4edda' : '#fff3cd',
                  borderRadius: '4px',
                  marginBottom: '1rem'
                }}>
                  <strong>{t('security.mfa.status')} </strong>
                  {mfaStatus?.mfaEnabled ? (
                    <span style={{ color: '#155724' }}>{t('security.mfa.enabled')}</span>
                  ) : (
                    <span style={{ color: '#856404' }}>{t('security.mfa.notEnabled')}</span>
                  )}
                  {mfaStatus?.mfaEnabledAt && (
                    <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                      {t('security.mfa.enabledOn', { date: new Date(mfaStatus.mfaEnabledAt).toLocaleDateString() })}
                    </div>
                  )}
                </div>

                {mfaStatus?.tenantMfaMode === 'required' && !mfaStatus?.mfaEnabled && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '1rem'
                  }}>
                    {t('security.mfa.required')}
                  </div>
                )}

                {mfaStatus?.mfaEnabled && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>{t('security.mfa.recoveryCodesRemaining')} </strong>
                    {mfaStatus.recoveryCodesRemaining || 0}
                    {(mfaStatus.recoveryCodesRemaining || 0) <= 2 && (
                      <span style={{ color: 'var(--color-danger)', marginLeft: '0.5rem' }}>
                        {t('security.mfa.considerRegenerating')}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
                {mfaStatus?.mfaEnabled ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null)
                        setStep('regenerate')
                      }}
                      style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {t('security.mfa.regenerateCodes')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null)
                        setStep('disable')
                      }}
                      style={{
                        padding: '0.5rem 1.5rem',
                        backgroundColor: 'var(--color-danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {t('security.mfa.disableMfa')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartSetup}
                    disabled={loading}
                    style={{
                      padding: '0.5rem 1.5rem',
                      backgroundColor: 'var(--color-success)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? t('buttons.loading') : t('security.mfa.enableMfa')}
                  </button>
                )}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('buttons.close')}
            </button>
          </div>

          {/* Change Password Section */}
          <div style={{
            borderTop: '1px solid #dee2e6',
            marginTop: '1.5rem',
            paddingTop: '1.5rem'
          }}>
            <h3 style={{ marginBottom: '0.5rem' }}>{t('security.password.title')}</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              {t('security.password.description')}
            </p>

            {!showChangePassword ? (
              <button
                type="button"
                onClick={() => setShowChangePassword(true)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('security.password.changeButton')}
              </button>
            ) : (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                {passwordError && (
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    marginBottom: '1rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    marginBottom: '1rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {t('security.password.changed')}
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {t('security.password.currentPassword')}
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {t('security.password.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <PasswordRequirements password={newPassword} />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {t('security.password.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <div style={{
                      color: 'var(--color-danger)',
                      fontSize: '0.75rem',
                      marginTop: '0.25rem'
                    }}>
                      {t('security.password.noMatch')}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false)
                      setCurrentPassword('')
                      setNewPassword('')
                      setConfirmPassword('')
                      setPasswordError(null)
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: 'var(--color-secondary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('buttons.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={changingPassword || !isPasswordValid(newPassword) || newPassword !== confirmPassword}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: 'var(--color-success)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: changingPassword ? 'not-allowed' : 'pointer',
                      opacity: changingPassword || !isPasswordValid(newPassword) || newPassword !== confirmPassword ? 0.6 : 1
                    }}
                  >
                    {changingPassword ? t('security.password.changing') : t('security.password.updateButton')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )
    }

    if (step === 'setup') {
      return (
        <>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              marginBottom: '1.5rem',
              borderRadius: '4px',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              {error}
            </div>
          )}

          <h3 style={{ marginBottom: '1rem' }}>{t('security.mfa.setup.title')}</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              <strong>{t('security.mfa.setup.step1')}</strong> {t('security.mfa.setup.step1Desc')}
            </p>

            {setupData?.qrCodeUrl && (
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <img
                  src={setupData.qrCodeUrl}
                  alt="QR Code for MFA setup"
                  style={{ maxWidth: '200px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            )}

            <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
              {t('security.mfa.setup.enterManually')}
            </p>
            <code style={{
              display: 'block',
              padding: '0.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              wordBreak: 'break-all',
              fontSize: '0.875rem'
            }}>
              {setupData?.secret}
            </code>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{t('security.mfa.setup.step2')}</strong> {t('security.mfa.setup.step2Desc')}
            </p>
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.25rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setError(null)
                setStep('status')
              }}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleVerifySetup}
              disabled={loading || verifyCode.length !== 6}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-success)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || verifyCode.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || verifyCode.length !== 6 ? 0.6 : 1
              }}
            >
              {loading ? t('security.mfa.setup.verifying') : t('security.mfa.setup.verifyEnable')}
            </button>
          </div>
        </>
      )
    }

    if (step === 'recovery-codes') {
      return (
        <>
          <h3 style={{ marginBottom: '1rem', color: 'var(--color-success)' }}>
            {t('security.mfa.recoveryCodes.title')}
          </h3>

          <div style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <strong>{t('security.mfa.recoveryCodes.warning')}</strong> {t('security.mfa.recoveryCodes.warningText')}
          </div>

          <div style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            marginBottom: '1rem',
            fontFamily: 'monospace'
          }}>
            {recoveryCodes.map((code, index) => (
              <div key={index} style={{ padding: '0.25rem 0' }}>
                {code}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={copyRecoveryCodes}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('security.mfa.recoveryCodes.copy')}
            </button>
            <button
              type="button"
              onClick={() => {
                setRecoveryCodes([])
                fetchMfaStatus()
              }}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-success)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('security.mfa.recoveryCodes.done')}
            </button>
          </div>
        </>
      )
    }

    if (step === 'disable') {
      return (
        <>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              marginBottom: '1.5rem',
              borderRadius: '4px',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              {error}
            </div>
          )}

          <h3 style={{ marginBottom: '1rem' }}>{t('security.mfa.disable.title')}</h3>

          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <strong>{t('security.mfa.disable.warning')}</strong> {t('security.mfa.disable.warningText')}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              {t('security.mfa.disable.authCode')}
            </label>
            <input
              type="text"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.25rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setError(null)
                setDisableCode('')
                setStep('status')
              }}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleDisableMfa}
              disabled={loading || disableCode.length !== 6}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-danger)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || disableCode.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || disableCode.length !== 6 ? 0.6 : 1
              }}
            >
              {loading ? t('security.mfa.disable.disabling') : t('security.mfa.disableMfa')}
            </button>
          </div>
        </>
      )
    }

    if (step === 'regenerate') {
      return (
        <>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              marginBottom: '1.5rem',
              borderRadius: '4px',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              {error}
            </div>
          )}

          <h3 style={{ marginBottom: '1rem' }}>{t('security.mfa.regenerate.title')}</h3>

          <div style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '4px',
            marginBottom: '1.5rem'
          }}>
            <strong>{t('security.mfa.regenerate.note')}</strong> {t('security.mfa.regenerate.noteText')}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              {t('security.mfa.disable.authCode')}
            </label>
            <input
              type="text"
              value={regenerateCode}
              onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1.25rem',
                textAlign: 'center',
                letterSpacing: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setError(null)
                setRegenerateCode('')
                setStep('status')
              }}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: 'var(--color-secondary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleRegenerateCodes}
              disabled={loading || regenerateCode.length !== 6}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || regenerateCode.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || regenerateCode.length !== 6 ? 0.6 : 1
              }}
            >
              {loading ? t('security.mfa.regenerate.regenerating') : t('security.mfa.regenerateCodes')}
            </button>
          </div>
        </>
      )
    }

    return null
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('security.title')} size="md">
      {renderContent()}
    </Modal>
  )
}

export default MfaSettingsModal
